import OpenAI from 'openai';
import type { GenerateOptions } from '../../types/generation';
import {
  getSystemPrompt,
  getUserPrompt,
  getResumeSystemPrompt,
  getResumeUserPrompt,
  generatePromptPrefix
} from './openai/prompts';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from './firebase/config';

const OPENAI_API_KEY = '1111';

const openai = new OpenAI({
  apiKey: OPENAI_API_KEY,
  dangerouslyAllowBrowser: true
});

const functions = getFunctions(app);

interface GenerateResponse {
  data: string | null;
  error?: string;
}

export async function generateCoverLetter({
  jobDescription,
  resumeContent,
  options
}: {
  jobDescription: string;
  resumeContent: string;
  options: GenerateOptions;
}): Promise<GenerateResponse> {
  try {
    const generateCoverLetterFunction = httpsCallable(functions, 'generateCoverLetter');
    const result = await generateCoverLetterFunction({
      jobDescription,
      resumeContent,
      options
    });

    return { data: result.data as string };
  } catch (error: any) {
    console.error('Cover letter generation error:', error);
    return {
      data: null,
      error: error.message || 'Failed to generate cover letter'
    };
  }
}

export async function generateResume({
  jobDescription,
  resumeContent,
  options
}: {
  jobDescription: string;
  resumeContent: string;
  options: GenerateOptions;
}): Promise<GenerateResponse> {
  try {
    const generateResumeFunction = httpsCallable(functions, 'generateResume');
    const result = await generateResumeFunction({
      jobDescription,
      resumeContent,
      options
    });

    return { data: result.data as string };
  } catch (error: any) {
    console.error('Resume generation error:', error);
    return {
      data: null,
      error: error.message || 'Failed to generate resume'
    };
  }
}

export async function formatCoverLetter({
  content
}: {
  content: string;
}): Promise<GenerateResponse> {
  try {
    const response = await openai.chat.completions.create({
      model: 'gpt-4',
      messages: [
        {
          role: 'system',
          content: `You are a professional document formatter. Format the provided cover letter for PDF export.

          CRITICAL REQUIREMENTS:
          1. DO NOT modify, rewrite, or alter the original text content in any way
          2. Preserve the exact wording and content as provided
          3. Only add formatting elements like proper spacing and line breaks
          4. Ensure content fits within standard margins (1 inch on all sides)
          5. Add today's date at the top in Month DD, YYYY format
          6. Use proper line spacing (1.15)
          7. Maintain left alignment
          8. Add one empty line between paragraphs
          9. DO NOT add any additional text, comments, or content
          10. Return ONLY the formatted text`
        },
        {
          role: 'user',
          content
        }
      ],
      temperature: 0.3
    });

    const formattedContent = response.choices[0]?.message?.content;
    if (!formattedContent) {
      throw new Error('No content generated');
    }

    return { data: formattedContent };
  } catch (error: any) {
    console.error('OpenAI API Error:', error);
    return {
      data: null,
      error: error.message || 'Failed to format cover letter'
    };
  }
}